import { useEffect, useMemo, useState } from "react";
import { Building2 } from "lucide-react";
import { AppShell } from "../layouts/AppShell";
import { EntityWorkspace } from "../layouts/EntityWorkspace";
import { EntityListSidebar } from "../layouts/EntityListSidebar";
import { EntityHeaderPattern } from "../patterns/EntityHeaderPattern";
import { DataTablePattern } from "../patterns/DataTablePattern";
import { Modal } from "../primitives/Modal";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";
import { Button } from "../primitives/Button";
import {
  createOrganization,
  listOrganizations,
  listOrgMemberships,
  updateOrganization,
} from "../../api/admin";

type AdminOrg = {
  id: string;
  name: string;
  type: string;
  status: string;
  city?: string;
  province?: string;
};

const ORG_TYPES = ["xlkey_internal", "farm", "ambassador_org", "drone_pilot_org"];

export function AdminOrganizationsPage({ userName = "Utilisateur" }: { userName?: string }) {
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<
    { id: string; user?: { email?: string }; org_role: string; status: string }[]
  >([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    type: "ambassador_org",
    status: "active",
    city: "",
    province: "",
  });

  useEffect(() => {
    let mounted = true;
    listOrganizations()
      .then((data) => {
        if (!mounted) return;
        setOrgs(data.items || []);
        if (data.items?.[0]?.id) {
          setSelectedId(data.items[0].id);
        }
      })
      .catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let mounted = true;
    listOrgMemberships(selectedId)
      .then((data) => {
        if (!mounted) return;
        setMemberships(data.items || []);
      })
      .catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, [selectedId]);

  const selectedOrg = useMemo(
    () => orgs.find((org) => org.id === selectedId) || null,
    [orgs, selectedId]
  );

  const listItems = useMemo(
    () =>
      orgs.map((org) => ({
        id: org.id,
        title: org.name,
        subtitle: org.type,
        info: org.status,
        icon: <Building2 size={16} strokeWidth={1.5} />,
        status: { label: org.status === "active" ? "Actif" : "Suspendu", tone: org.status === "active" ? "success" : "warning" },
      })),
    [orgs]
  );

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    const created = await createOrganization({
      name: form.name,
      type: form.type,
      status: form.status,
      city: form.city,
      province: form.province,
    });
    setOrgs((prev) => [created, ...prev]);
    setSelectedId(created.id);
    setOpen(false);
    setForm({ name: "", type: "ambassador_org", status: "active", city: "", province: "" });
  };

  const handleUpdate = async () => {
    if (!selectedOrg) return;
    const updated = await updateOrganization(selectedOrg.id, {
      name: form.name,
      status: form.status,
    });
    setOrgs((prev) => prev.map((org) => (org.id === updated.id ? updated : org)));
    setEditOpen(false);
  };

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Admin • Organisations</div>}
      userName={userName}
      modules={[
        { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
        { label: "Clients", onClick: () => (window.location.hash = "#/clients") },
        { label: "Ambassadeurs", onClick: () => (window.location.hash = "#/ambassadors") },
        { label: "Pilotes", onClick: () => (window.location.hash = "#/pilots") },
        { label: "Soumissions" },
        { label: "Factures" },
        { label: "KPIs" },
        { label: "Paramètres" },
        { label: "Admin • Users", onClick: () => (window.location.hash = "#/admin") },
        { label: "Admin • Orgs", onClick: () => (window.location.hash = "#/admin-orgs") },
      ]}
    >
      <EntityWorkspace
        header={
          selectedOrg ? (
            <EntityHeaderPattern
              title={selectedOrg.name}
              metaItems={[
                { label: "Type", value: selectedOrg.type, tone: "default" },
                { label: "Statut", value: selectedOrg.status, tone: "muted" },
              ]}
              status={{ label: selectedOrg.status === "active" ? "Actif" : "Suspendu", tone: selectedOrg.status === "active" ? "success" : "warning" }}
              primaryAction={{
                label: "Nouvelle organisation",
                onClick: () => setOpen(true),
              }}
              secondaryActions={[
                {
                  label: "Modifier",
                  onClick: () => {
                    setForm({
                      name: selectedOrg.name,
                      type: selectedOrg.type,
                      status: selectedOrg.status,
                      city: selectedOrg.city || "",
                      province: selectedOrg.province || "",
                    });
                    setEditOpen(true);
                  },
                },
              ]}
            />
          ) : null
        }
        sidebarTitle="Organisations"
        sidebarIcon="🏢"
        sidebarMeta={orgs.length ? `${orgs.length}` : undefined}
        sidebarItems={[]}
        allowHoverOpen
        renderSidebar={(isExpanded) => (
          <EntityListSidebar
            sections={[]}
            items={listItems}
            selectedId={selectedId}
            onSelect={(id) => setSelectedId(id)}
            isExpanded={isExpanded}
            searchPlaceholder="Rechercher une organisation"
            filterOptions={["Tous", "Actif", "Suspendu"]}
            emptyMessage="Aucune organisation disponible."
            addActionLabel="+ Ajouter"
            onAddAction={() => setOpen(true)}
          />
        )}
        content={
          selectedOrg ? (
            <DataTablePattern
              title="Membres"
              columns={[
                { key: "email", label: "Utilisateur", type: "text" },
                { key: "role", label: "Rôle", type: "text" },
                { key: "status", label: "Statut", type: "text" },
              ]}
              rows={memberships.map((membership) => ({
                id: membership.id,
                email: membership.user?.email ?? "—",
                role: membership.org_role,
                status: membership.status,
              }))}
              rowActions={[{ label: "Modifier" }]}
            />
          ) : (
            <div className="rounded-md border border-border bg-surface p-6 shadow-sm text-sm text-muted">
              Aucune organisation sélectionnée.
            </div>
          )
        }
      />

      <Modal title="Créer une organisation" open={open} onClose={() => setOpen(false)}>
        <div className="space-y-3">
          <Input label="Nom" placeholder="Nom de l'organisation" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          <Select label="Type" options={ORG_TYPES} value={form.type} onChange={(value) => setForm({ ...form, type: value })} />
          <Select label="Statut" options={["active", "suspended"]} value={form.status} onChange={(value) => setForm({ ...form, status: value })} />
          <Input label="Ville" placeholder="Ville" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
          <Input label="Province" placeholder="QC" value={form.province} onChange={(event) => setForm({ ...form, province: event.target.value })} />
          <div className="flex justify-end">
            <Button label="Créer" intent="primary" size="sm" onClick={handleCreate} />
          </div>
        </div>
      </Modal>

      <Modal title="Modifier l'organisation" open={editOpen} onClose={() => setEditOpen(false)}>
        <div className="space-y-3">
          <Input label="Nom" placeholder="Nom de l'organisation" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          <Select label="Statut" options={["active", "suspended"]} value={form.status} onChange={(value) => setForm({ ...form, status: value })} />
          <div className="flex justify-end">
            <Button label="Enregistrer" intent="primary" size="sm" onClick={handleUpdate} />
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
